/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Box, Grid, Skeleton, Typography } from '@mui/material';
import stockIcon from 'Images/stock_icon.svg';
import equivalentIcon from 'Images/equivalent_copima.svg';
import rechercheExterneIcon from 'Images/recherche_externe_icon.svg';
import ProductCard from 'Shared/ProductCard';
import { searchProducts } from './SearchService';

/* ── Map search API response to ProductCard expected format ──── */
export const mapProduct = (p) => ({
  id: p.id,
  reference: p.reference,
  name: p.name,
  price: p.price || 0,
  remise: p.remise,
  remiseExceptionnelle: p.remiseExceptionnelle,
  stockBouskoura: p.stockBouskoura || 0,
  stockDerbOmar: p.stockDerbOmar || 0,
  stockAinSebaa: p.stockAinSebaa || 0,
  stockCfcim: p.stockCfcim || 0,
  reserved: 0,
  reservedDO: 0,
  brand: { name: p.brandName || p.brandId, logoImage: p.brandLogoImage },
  family: { name: p.familyName, familyPicture: p.familyPicture },
  familyId: p.familyId,
  level1FamilyId: p.level1FamilyId,
  image: p.image || (p.images && p.images.length > 0 ? p.images[0].downloadUrl : null),
  promo: p.promo,
  isNew: p.isNew || p.new,
  origin: p.origin,
  attributes: p.attributes,
  images: p.images,
});

/* ── Section header for search result groups ─────────────────── */
const ResultSectionHeader = ({ icon, title, count }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '8px', mt: '8px' }}>
    <img src={icon} alt={title} style={{ width: 24, height: 24, objectFit: 'contain' }} />
    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#385576' }}>
      {title}
      <Box component="span" sx={{ fontSize: '18px', fontWeight: 700, color: '#385576', ml: '6px' }}>
        ({count})
      </Box>
    </Typography>
  </Box>
);

/* ── Skeleton card matching ProductCard dimensions ──────────── */
const SkeletonCard = () => (
  <Box sx={{ backgroundColor: '#fff', border: '1.5px solid #D1D5DC', borderRadius: '12px', overflow: 'hidden', height: '422px' }}>
    <Skeleton variant="rectangular" height={228} />
    <Box sx={{ p: '12px 14px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '8px' }}>
        <Skeleton variant="text" width={60} height={16} />
        <Skeleton variant="text" width={80} height={16} />
      </Box>
      <Skeleton variant="text" width="90%" height={18} />
      <Skeleton variant="text" width="70%" height={18} sx={{ mb: '6px' }} />
      <Skeleton variant="text" width="50%" height={16} />
      <Skeleton variant="text" width="40%" height={14} sx={{ mb: '8px' }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box>
          <Skeleton variant="text" width={100} height={28} />
          <Skeleton variant="text" width={70} height={16} />
        </Box>
        <Skeleton variant="rectangular" width={34} height={34} sx={{ borderRadius: '8px' }} />
      </Box>
    </Box>
  </Box>
);

/* ── Skeleton for a full section (header + card grid) ──────── */
const SkeletonSection = () => (
  <Box sx={{ mb: '32px' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '8px', mt: '8px' }}>
      <Skeleton variant="circular" width={24} height={24} />
      <Skeleton variant="text" width={180} height={24} />
    </Box>
    <Grid container spacing={3}>
      {[...Array(5)].map((_, i) => (
        <Grid item xs={6} sm={4} md={2.4} key={i}>
          <SkeletonCard />
        </Grid>
      ))}
    </Grid>
  </Box>
);

/* ── Search results with 3 grouped sections ─────────────────── */
const SearchResults = ({
  search, searchVersion, onOpenDetail, onAddToCart, onTotalChange, onLoadingChange,
  familyFilters, brandFilters, onSearchCountsChange,
  priceMin, priceMax, productFilters,
}) => {
  const [stockCopima, setStockCopima] = useState([]);
  const [equivalentCopima, setEquivalentCopima] = useState([]);
  const [rechercheExterne, setRechercheExterne] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!search) return;
    setLoading(true);
    if (onLoadingChange) onLoadingChange(true);
    searchProducts(search)
      .then((data) => {
        const rawSc = data?.stockCopima || [];
        const rawEq = data?.equivalentCopima || [];
        const rawRe = data?.rechercheExterne || [];

        /* compute counts from all raw results */
        const familyIdMap = {};
        const brandCountMap = {};
        let countNew = 0, countPromo = 0, countAvailable = 0;
        const mapped = [...rawSc.map(mapProduct), ...rawEq.map(mapProduct), ...rawRe.map(mapProduct)];
        mapped.forEach((p) => {
          if (p.level1FamilyId) familyIdMap[p.level1FamilyId] = (familyIdMap[p.level1FamilyId] || 0) + 1;
          if (p.brand?.name) brandCountMap[p.brand.name] = (brandCountMap[p.brand.name] || 0) + 1;
          if (p.isNew) countNew++;
          if (p.promo) countPromo++;
          const stock = (p.stockBouskoura || 0) + (p.stockDerbOmar || 0) + (p.stockAinSebaa || 0) + (p.stockCfcim || 0);
          if (stock > 0) countAvailable++;
        });
        if (onSearchCountsChange) {
          onSearchCountsChange({
            families: Object.entries(familyIdMap).map(([id, count]) => ({ id, count })),
            brands: Object.entries(brandCountMap).map(([name, count]) => ({ name, count })),
            isNew: countNew,
            isPromo: countPromo,
            isAvailable: countAvailable,
          });
        }

        setStockCopima(mapped.slice(0, rawSc.length));
        setEquivalentCopima(mapped.slice(rawSc.length, rawSc.length + rawEq.length));
        setRechercheExterne(mapped.slice(rawSc.length + rawEq.length));
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
        if (onLoadingChange) onLoadingChange(false);
      });
  }, [search, searchVersion]);

  /* apply client-side filters — brand+family always apply; price/new/promo/available skip external results */
  const applyFilters = (list, isExternal = false) => list.filter((p) => {
    if (familyFilters && familyFilters.length > 0 && !familyFilters.includes(p.level1FamilyId)) return false;
    if (brandFilters && brandFilters.length > 0 && !brandFilters.includes(p.brand?.name)) return false;
    if (isExternal) return true;
    if (priceMin !== '' && priceMin != null && p.price < Number(priceMin)) return false;
    if (priceMax !== '' && priceMax != null && p.price > Number(priceMax)) return false;
    if (productFilters && productFilters.includes('new') && !p.isNew) return false;
    if (productFilters && productFilters.includes('promo') && !p.promo) return false;
    if (productFilters && productFilters.includes('available')) {
      const totalStock = (p.stockBouskoura || 0) + (p.stockDerbOmar || 0) + (p.stockAinSebaa || 0) + (p.stockCfcim || 0);
      if (totalStock <= 0) return false;
    }
    return true;
  });

  const filteredSc = applyFilters(stockCopima);
  const filteredEq = applyFilters(equivalentCopima);
  const filteredRe = applyFilters(rechercheExterne, true);

  /* keep total count and sidebar counts in sync with active filters */
  useEffect(() => {
    if (loading) return;
    if (onTotalChange) onTotalChange(filteredSc.length + filteredEq.length + filteredRe.length);
    if (onSearchCountsChange) {
      const allFiltered = [...filteredSc, ...filteredEq];
      const familyIdMap = {};
      const brandCountMap = {};
      let countNew = 0, countPromo = 0, countAvailable = 0;
      allFiltered.forEach((p) => {
        if (p.level1FamilyId) familyIdMap[p.level1FamilyId] = (familyIdMap[p.level1FamilyId] || 0) + 1;
        if (p.brand?.name) brandCountMap[p.brand.name] = (brandCountMap[p.brand.name] || 0) + 1;
        if (p.isNew) countNew++;
        if (p.promo) countPromo++;
        const stock = (p.stockBouskoura || 0) + (p.stockDerbOmar || 0) + (p.stockAinSebaa || 0) + (p.stockCfcim || 0);
        if (stock > 0) countAvailable++;
      });
      onSearchCountsChange((prev) => ({
        ...prev,
        families: Object.entries(familyIdMap).map(([id, count]) => ({ id, count })),
        brands: Object.entries(brandCountMap).map(([name, count]) => ({ name, count })),
        isNew: countNew,
        isPromo: countPromo,
        isAvailable: countAvailable,
      }));
    }
  }, [familyFilters, brandFilters, priceMin, priceMax, productFilters, stockCopima, equivalentCopima, rechercheExterne, loading]);

  if (loading) {
    return (
      <>
        <SkeletonSection />
        <SkeletonSection />
        <SkeletonSection />
      </>
    );
  }

  return (
    <>
      {/* Stock Copima */}
      {filteredSc.length > 0 && (
        <>
          <ResultSectionHeader
            icon={stockIcon}
            title="Stock Copima"
            count={filteredSc.length}
          />
          <Grid container spacing={3} sx={{ mb: '32px' }}>
            {filteredSc.map((product) => (
              <Grid item xs={6} sm={4} md={2.4} key={product.id || product.reference}>
                <ProductCard product={product} onClick={onOpenDetail} onAddToCart={onAddToCart} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Équivalent Copima */}
      {filteredEq.length > 0 && (
        <>
          <ResultSectionHeader
            icon={equivalentIcon}
            title="Équivalent Copima"
            count={filteredEq.length}
          />
          <Grid container spacing={3} sx={{ mb: '32px' }}>
            {filteredEq.map((product) => (
              <Grid item xs={6} sm={4} md={2.4} key={product.id || product.reference}>
                <ProductCard product={product} onClick={onOpenDetail} onAddToCart={onAddToCart} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Recherche Externe */}
      {filteredRe.length > 0 && (
        <>
          <ResultSectionHeader
            icon={rechercheExterneIcon}
            title="Recherche Externe"
            count={filteredRe.length}
          />
          <Grid container spacing={3}>
            {filteredRe.map((product) => (
              <Grid item xs={6} sm={4} md={2.4} key={product.id || product.reference}>
                <Box sx={{
                  position: 'relative',
                  '& > div': {
                    borderColor: '#9D5BEF',
                    '&:hover': { borderColor: '#9D5BEF', boxShadow: '0px 10px 32px rgba(157,91,239,0.15)' },
                  },
                  '& > div > div:nth-of-type(2)': { backgroundColor: '#9D5BEF' },
                }}>
                  <Box sx={{
                    position: 'absolute', top: 10, left: 10, zIndex: 2,
                    backgroundColor: 'rgba(157, 91, 239, 0.14)', color: '#9D5BEF',
                    borderRadius: '30px', px: '8px', py: '3px',
                    fontSize: '11px', fontWeight: 700, letterSpacing: '0.3px',
                  }}>
                    Résultat Externe
                  </Box>
                  <ProductCard product={{ ...product, family: null }} hidePrice onClick={onOpenDetail} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* No results */}
      {filteredSc.length === 0 && filteredEq.length === 0 && filteredRe.length === 0 && (
        <Typography sx={{ textAlign: 'center', py: '40px', color: '#8397AE', fontSize: '14px' }}>
          Aucun résultat trouvé
        </Typography>
      )}
    </>
  );
};

export default SearchResults;
